import { useEffect, useState } from 'react';

/**
 * The six numbers from the report that a person reviewing a sheet can act on.
 *
 * The report holds far more -- every threshold, every tile, the whole lattice
 * census -- and showing all of it would mean none of it gets read. These are
 * the ones that answer "should this be printed": how far the weave departed
 * from a regular grid, whether every inked stitch has canvas under it, whether
 * the canvas is the count the design expects, and how much margin is left.
 *
 * Each is shown against its limit, because a residual of 0.31 means nothing on
 * its own and everything next to a tolerance of 0.5.
 */
export interface Report {
  orientation_chosen?: {
    rotation_deg?: number;
    settled_in_page_coordinates?: boolean;
    preview_rotation_deg?: number;
    design_down_dy_in?: number | null;
  };
  lattice?: {
    homography_residual_max_pitch?: number;
    residual_tolerance_pitch?: number;
    loop_closure_failures?: number;
    cells_measured?: number;
    max_bridge_span?: number;
    max_bridge_allowed?: number;
  };
  coverage?: { inked_cells?: number; inked_cells_unsupported?: number };
  mesh_count?: { detected?: number; art_stitches_per_inch?: number };
  placement?: { margin_inches_per_side?: number[] };
  print?: { cells?: number; inks?: number; off_page_cut_lines?: number };
  photo?: { px_per_hole?: number };
}

/**
 * Fetch one sheet's report once.
 *
 * Shared with the review page, which needs the same document to say which way
 * up the sheet will print. Two components fetching the same signed URL would
 * work and would also be two chances to disagree about what it said.
 */
export function useReport(url: string | null) {
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    fetch(url)
      .then((r) => r.json())
      .then((data: Report) => { if (!cancelled) setReport(data); })
      .catch((e: Error) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [url]);

  return { report, error };
}

export function ReportSummary(
  { report, error }: { report: Report | null; error: string | null },
) {
  if (error) return <p className="muted">Could not load the report: {error}</p>;
  if (!report) return <p className="muted">Loading the report…</p>;

  const lattice = report.lattice ?? {};
  const coverage = report.coverage ?? {};
  const margin = report.placement?.margin_inches_per_side ?? [];
  const unsupported = coverage.inked_cells_unsupported ?? 0;

  const rows: { label: string; value: string; bad?: boolean }[] = [
    {
      label: 'Weave regularity',
      value: `${fmt(lattice.homography_residual_max_pitch)} of `
        + `${fmt(lattice.residual_tolerance_pitch)} pitches allowed`,
      bad: over(lattice.homography_residual_max_pitch,
                lattice.residual_tolerance_pitch),
    },
    {
      label: 'Holes measured',
      value: `${lattice.cells_measured?.toLocaleString() ?? '—'}`
        + `, ${lattice.loop_closure_failures ?? 0} conflicts`,
    },
    {
      label: 'Longest gap bridged',
      value: `${lattice.max_bridge_span ?? '—'} of `
        + `${lattice.max_bridge_allowed ?? '—'} allowed`,
      bad: over(lattice.max_bridge_span, lattice.max_bridge_allowed),
    },
    {
      label: 'Stitches on bare canvas',
      value: unsupported === 0
        ? `all ${coverage.inked_cells?.toLocaleString() ?? '—'}`
        : `${unsupported} of ${coverage.inked_cells} have no canvas`,
      bad: unsupported > 0,
    },
    {
      label: 'Canvas measured',
      value: `${fmt(report.mesh_count?.detected, 1)} threads/inch against `
        + `${fmt(report.mesh_count?.art_stitches_per_inch, 0)} authored`,
    },
    {
      label: 'Margin per side',
      value: margin.length === 2
        ? `${fmt(margin[0], 2)}" × ${fmt(margin[1], 2)}"`
        : '—',
    },
  ];

  if (report.print) {
    rows.push({
      label: 'On the print file',
      value: `${report.print.cells?.toLocaleString() ?? '—'} stitches in `
        + `${report.print.inks ?? '—'} ink(s)`,
    });
  }

  return (
    <dl className="facts">
      {rows.map((row) => (
        <div key={row.label}>
          <dt>{row.label}</dt>
          <dd className={row.bad ? 'warn-text' : undefined}>{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function fmt(value: number | undefined, places = 2): string {
  return typeof value === 'number' ? value.toFixed(places) : '—';
}

function over(value: number | undefined, limit: number | undefined): boolean {
  return typeof value === 'number' && typeof limit === 'number' && value > limit;
}
